import { Product } from '@axo/shared/data-access/types';
import { DataSharingStatus, MarketCountryCode } from '@axo/shared/types';
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Step } from '../../types';
import { addToQueryParams } from '../../utils/navigation';
import { useBroker } from '../useBroker';
import getCurrentStep from './utils/getCurrentStep';
import { getNumberOfCompletedSteps } from './utils/getNumberOfCompletedSteps';
import getQueryStep from './utils/getQueryStep';

export type UseStepsProps = {
  initialized: boolean;
  steps: Step[];
  reloadedData?: Record<string, unknown>;
  marketCountry: MarketCountryCode;
  isPre?: boolean;
  isWL?: boolean;
  formRef?: RefObject<HTMLFormElement>;
  product?: Product;
  dataSharingStatus?: DataSharingStatus;
};

/**
 * This hook must return the current step, the current step setter, the current step component and the number of completed steps by the user.
 */
export const useSteps = ({
  initialized,
  steps,
  reloadedData,
  marketCountry,
  isPre = false,
  isWL = false,
  formRef,
  product,
  dataSharingStatus,
}: UseStepsProps) => {
  const [step, _setStep] = useState<number>(1);
  const [isStepSet, setIsStepSet] = useState(false);

  const scrollToRef = useRef<NodeJS.Timeout>();

  const broker = useBroker();

  const hasReloadedData = useMemo(
    () => !!(reloadedData && Object.keys(reloadedData).length > 0),
    [reloadedData]
  );

  const numberOfCompletedSteps = useMemo(
    () =>
      hasReloadedData
        ? getNumberOfCompletedSteps({
            marketCountry,
            reloadedData,
            product,
            broker,
            amountOfSteps: steps?.length,
            dataSharingStatus,
          })
        : 0,
    [
      marketCountry,
      reloadedData,
      hasReloadedData,
      product,
      broker,
      steps,
      dataSharingStatus,
    ]
  );

  const setStep = useCallback(
    (newStep: number, shouldSetQueryParams = true) => {
      if (isPre) return;

      _setStep(newStep < 1 ? 1 : newStep);

      if (shouldSetQueryParams)
        addToQueryParams({
          newParams: {
            step: newStep.toString(),
          },
        });

      if (isWL && formRef?.current) {
        scrollToRef.current = setTimeout(() => {
          formRef?.current?.scrollIntoView({
            behavior: 'smooth',
          });
        }, 100);
        return;
      }

      scrollToRef.current = setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 100);
    },
    [_setStep, isPre, formRef, isWL]
  );

  const updateStepWithQueryParams = useCallback(
    (event: PopStateEvent) => {
      const curStep = +event.state?.['step'] || 1;
      setStep(curStep, false);
    },
    [setStep]
  );

  useEffect(() => {
    if (isStepSet) return;

    if (isPre && step !== 1) {
      setIsStepSet(true);
      setStep(1);
      return;
    }

    if (!initialized) return;

    const queryStep = getQueryStep();

    let newStep = getCurrentStep({
      queryStep,
      hasReloadedData,
      currentStep: step,
      numberOfCompletedSteps,
      amountOfSteps: steps?.length,
    });

    // TODO: Remove after ESkat is permanently enabled in form DK
    if (
      marketCountry === MarketCountryCode.Denmark &&
      dataSharingStatus === DataSharingStatus.Disabled &&
      newStep > 2 &&
      numberOfCompletedSteps < steps.length &&
      queryStep !== newStep
    ) {
      newStep -= 1;
    }

    setStep(newStep, hasReloadedData);
    setIsStepSet(true);
  }, [
    hasReloadedData,
    numberOfCompletedSteps,
    step,
    isStepSet,
    setStep,
    isPre,
    steps,
    initialized,
    dataSharingStatus,
    marketCountry,
  ]);

  useEffect(() => {
    if (hasReloadedData) setIsStepSet(false);
  }, [hasReloadedData, reloadedData]);

  useEffect(() => {
    return () => {
      if (scrollToRef.current) clearTimeout(scrollToRef.current);
    };
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', updateStepWithQueryParams);

    return () => {
      window.removeEventListener('popstate', updateStepWithQueryParams);
    };
  }, [updateStepWithQueryParams]);

  const StepComponent = useMemo(
    () => steps.at(step - 1)?.component || steps.at(-1)?.component,
    [step, steps]
  );

  return useMemo(
    () => ({
      step,
      setStep,
      StepComponent,
      numberOfCompletedSteps,
    }),
    [step, StepComponent, numberOfCompletedSteps, setStep]
  );
};
